import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import loadable from '@loadable/component';
import { makeStyles } from 'tss-react/mui';
import { Link } from 'react-router-dom';
import TagList from '@components/UI/TagList';
import useGame from '@hooks/API/useGame';
import TournamentBoxBanner from './TournamentBoxBanner';
import TournamentPhaseBannerSimple from '@domain/Tournament/TournamentDetails/TournamentPhaseBannerAlert/TournamentPhaseBannerSimple';
import GamesPlayed from '@components/Tournament/GamesPlayed';
import doTrackEvent, { extractTournamentAnalyticsData } from '@components/Analytics/doTrackEvent';
import useTournamentDetailsPageUrl from '@hooks/useTournamentDetailsPageUrl';
import withTournamentV2 from '@domain/TournamentV2/withTournamentV2';
import TournamentBoxCompact from '@domain/TournamentV2/TournamentTable/TournamentBoxCompact';
import useAppState from '@hooks/API/useAppState';
import { getTournamentColorPaletteName, getTournamentState } from '@domain/Tournament/helpers/tournamentUtils';
import getTournamentStatusVariant from '@domain/Tournament/helpers/getTournamentStatusVariant';
import getTournamentBoxBannerImage from '@domain/Tournament/helpers/getTournamentBoxBannerImage';
import getTournamentDefaultBanner from '@domain/Tournament/helpers/getTournamentDefaultBanner';
import getTournamentServersDisplay from '@domain/Tournament/helpers/getTournamentServersDisplay';
import getTournamentModesDisplay from '@domain/Tournament/helpers/getTournamentModesDisplay';
import StatusIndicator from '@components/UI/StatusIndicator';
import SROnly from '@components/UI/SROnly';
import Currency from '@components/UI/Currency';
const GameIcon = loadable(() => import('@components/Icons/GameIcon'));
const useStyles = makeStyles()((theme) => ({
    tournamentBoxletteContainer: {
        padding: theme.spacing(1.5, 0),
        display: 'block',
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
    },
    tournamentContent: {
        display: 'flex',
        marginBottom: theme.spacing(1.5),
    },
    tournamentBanner: {
        height: '77px',
        width: '140px',
        borderRadius: '4px',
        marginRight: theme.spacing(2),
        padding: theme.spacing(1),
        flexShrink: 0,
    },
    tournamentTimer: {
        display: 'block',
        marginBottom: theme.spacing(1),
        '& span': {
            fontWeight: 600,
            color: theme.palette.grey[400],
            fontSize: '10px',
        },
    },
    tournamentEntryDetails: {
        display: 'flex',
    },
    bannerStatus: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    gameIcon: {
        background: 'white',
        borderRadius: theme.shape.borderRadius,
        padding: '2px',
        width: '17px',
        height: '17px',
    },
    tournamentStatus: {
        '& span': {
            width: 13,
            height: 13,
            border: '2px solid white',
            boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.35)',
            margin: 0,
        },
    },
    tournamentName: {
        fontSize: '11px',
        color: theme.palette.grey[900],
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    metricWrapper: {
        marginRight: theme.spacing(2),
        '& p:first-child': {
            fontSize: 10,
            fontWeight: 600,
            color: theme.palette.grey[400],
            marginBottom: theme.spacing(0.5),
        },
    },
    metricText: {
        fontSize: 11,
        fontWeight: 600,
        color: theme.palette.grey[900],
        lineHeight: '11px',
    },
    entryFeeFree: {
        fontSize: 11,
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    gamesPlayed: {
        '& span': {
            fontSize: 11,
        },
    },
    tournamentSlots: {
        color: theme.palette.grey[400],
    },
}));
const TournamentBoxlette = ({ tournament, className, section, listIndex, testId }) => {
    var _a, _b, _c, _d;
    const paletteName = getTournamentColorPaletteName(tournament);
    const { classes, cx } = useStyles();
    const gameKey = tournament.score_template.game.game_key;
    const gameData = useGame(gameKey);
    const detailsLink = useTournamentDetailsPageUrl(tournament.id, gameData);
    const appState = useAppState();
    const tournamentState = getTournamentState(tournament, appState.registrationEndingMinutes);
    const tournamentStatus = getTournamentStatusVariant(tournamentState);
    const tournamentImageUrl = getTournamentBoxBannerImage(tournament);
    const defaultTournamentImageUrl = gameKey ? getTournamentDefaultBanner(gameKey, tournament, true) : '';
    const tournamentCurrency = tournament.currencyType.abbreviation;
    const hasEntered = !!tournament.user_entry;
    const isMatchmakingLeaderboard = !!(tournament === null || tournament === void 0 ? void 0 : tournament.leaderboardTemplate);
    const tournamentTags = [{ icon: 'server', label: getTournamentServersDisplay(tournament, gameData) }];
    const tournamentModes = getTournamentModesDisplay(tournament);
    if (tournamentModes) {
        tournamentTags.push({ icon: 'trophy', label: tournamentModes });
    }
    const onBoxClick = () => {
        doTrackEvent({
            ...extractTournamentAnalyticsData(tournament, appState.registrationEndingMinutes),
            type: 'open_tournament',
            section,
            listIndex: listIndex.toString(),
            viewType: 'boxlette',
        });
    };
    const finished = tournamentState === 'closed' || tournamentState === 'finished';
    return (_jsxs(Link, { to: detailsLink, onClick: onBoxClick, className: cx(classes.tournamentBoxletteContainer, className), "data-testid": testId, children: [_jsxs("div", { className: cx(classes.tournamentContent), children: [_jsx(TournamentBoxBanner, { bannerImage: tournamentImageUrl || defaultTournamentImageUrl, paletteName: paletteName, className: classes.tournamentBanner, children: _jsxs("div", { className: classes.bannerStatus, children: [_jsx(GameIcon, { optimise: true, game: tournament.score_template.game.game_key, className: classes.gameIcon }), _jsx(StatusIndicator, { status: tournamentStatus, className: classes.tournamentStatus, children: _jsx(SROnly, { children: `${tournamentState.charAt(0).toUpperCase()}${tournamentState.slice(1)}` }) })] }) }), _jsxs("div", { children: [_jsx(TournamentPhaseBannerSimple, { tournament: tournament, className: classes.tournamentTimer }), _jsx("h4", { className: classes.tournamentName, "data-notranslate": true, children: tournament.name }), _jsx(TagList, { tags: tournamentTags })] })] }), _jsxs("div", { className: classes.tournamentEntryDetails, children: [_jsxs("div", { className: classes.metricWrapper, children: [_jsx("p", { children: "Entry Fee" }), _jsxs("p", { className: cx(tournament.entry_fee === 0 ? classes.entryFeeFree : classes.metricText), children: [tournament.entry_fee === 0 && 'Free Entry', tournament.entry_fee !== 0 && (_jsx(Currency, { optimise: true, amount: tournament.entry_fee, currency: tournamentCurrency }))] })] }), !tournament.finished && (_jsxs("div", { className: classes.metricWrapper, children: [_jsx("p", { children: isMatchmakingLeaderboard ? 'Prize Pool Per Leaderboard' : 'Prize Pool' }), _jsx("p", { className: classes.metricText, children: _jsx(Currency, { optimise: true, showCompactVersion: true, amount: tournament.prize_pot, currency: tournamentCurrency, payoutTemplate: tournament.payout_template }) })] })), tournament.finished && (_jsxs("div", { className: classes.metricWrapper, children: [_jsx("p", { children: "Earnings" }), _jsx("p", { className: classes.metricText, children: _jsx(Currency, { optimise: true, amount: ((_a = tournament === null || tournament === void 0 ? void 0 : tournament.user_entry) === null || _a === void 0 ? void 0 : _a.paidOutValue) && ((_b = tournament === null || tournament === void 0 ? void 0 : tournament.user_entry) === null || _b === void 0 ? void 0 : _b.eligible)
                                        ? tournament.user_entry.paidOutValue
                                        : 0, currency: tournamentCurrency }) })] })), !hasEntered &&
                        (tournament.slots ? (_jsxs("div", { className: classes.metricWrapper, children: [_jsx("p", { children: "Entrants" }), _jsxs("p", { className: classes.metricText, "data-notranslate": true, children: [tournament.entries_count.toLocaleString(undefined), _jsxs("span", { className: classes.tournamentSlots, children: [" / ", tournament.slots.toLocaleString(undefined)] })] })] })) : (_jsxs("div", { className: classes.metricWrapper, children: [_jsx("p", { children: finished ? 'Received prizes' : 'Receives prizing' }), _jsxs("p", { className: classes.metricText, children: [!finished && 'Top', " ", (_c = tournament.maximum_prize_positions) === null || _c === void 0 ? void 0 : _c.toLocaleString(), " Players"] })] }))), hasEntered && (_jsxs("div", { className: classes.metricWrapper, children: [_jsx("p", { children: "Qualification" }), _jsx("p", { className: classes.metricText, "aria-label": "Eligibility", children: _jsx(GamesPlayed, { className: classes.gamesPlayed, gamesPlayed: ((_d = tournament === null || tournament === void 0 ? void 0 : tournament.user_entry) === null || _d === void 0 ? void 0 : _d.gamesPlayed) || 0, requiredGames: tournament.score_template.num_games || 1 }) })] }))] })] }));
};
export default withTournamentV2(TournamentBoxlette, TournamentBoxCompact);
